export const SECTION_NAME = "ItemReceipt";

export const INTRO_TEXT = `Items are received into inventory by creating item
  receipts.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "IR-12345",
    "date": "2019-05-08T09:13:00",
    "vendor": {
      "id": 23,
      "name": "ABC Plastics"
    },
    "location": {
      "id": 1,
      "name": "Warehouse"
    },
    "terms": {
      "id": 11,
      "name": "NET 30"
    },
    "department": null,
    "currency": {
      "id": 42,
      "name": "USD"
    },
    "taxCode": {
      "id": 5,
      "name": "Sales Tax"
    },
    "linkedTransaction": null,
    "exchangeRate": 1,
    "vendorMessage": null,
    "comment": null,
    "vendorNotes": "",
    "payment": "Bill",
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Our-Acct-No",
        "value": "ABC84729",
        "dataType": "String"
      }
    ],
    "depositPercent": 0,
    "depositAmount": 0,
    "subTotal": 12,
    "total": 12,
    "overhead": 0.00000,
    "archived": false,
    "summaryOnly": false,
    "hasSignature": false,
    "autoSerialLots": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "vendorPartNumber": null,
        "class": null,
        "job": null,
        "workcenter": null,
        "tax": {
          "taxable": false,
          "taxCode": null
        },
        "linkedTransaction": null,
        "description": null,
        "quantity": 1,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 12,
        "amount": 12,
        "received": 0,
        "uom": {
          "id": 1,
          "name": "each"
        },
        "bin": null,
        "lot": {
          "id": 17,
          "name": "B24XP9381"
        },
        "lotExpiration": null,
        "serials": [
          {
            "id": 837,
            "name": "48147G489"
          },
          {
            "id": 845,
            "name": "48147G498"
          }
        ]
      }
    ],
    "otherCosts": [
      {
        "id": 128,
        "lineNumber": 1,
        "item": {
          "id": 1679,
          "name": "TestLead"
        },
        "vendor": {
          "id": 94,
          "name": "Horizon (2)"
        },
        "class": {
          "id": 8,
          "name": "No Class"
        },
        "amount": 2.10000,
        "bill": true
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this item receipt has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a item receipt, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "vendor",
    type: "reference",
    description: "Vendor from which these items were received.",
  },
  {
    name: "location",
    type: "reference",
    description: `Location where these items were received.`,
  },
  {
    name: "terms",
    type: "reference",
    description: `Payment terms.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "currency",
    type: "reference",
    description: `Currency used for transaction if multicurrency is enabled.`,
  },
  {
    name: "taxCode",
    type: "reference",
    description: `Tax code for transaction.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this item receipt.`,
  },
  {
    name: "exchangeRate",
    type: "decimal",
    description: `The exchange rate used for this transaction, if multicurrency is enabled.`,
  },
  {
    name: "vendorMessage",
    type: "string",
    description: `Vendor message field.`,
  },
  {
    name: "comment",
    type: "string",
    description: `Comment field about transaction. For in-house use.`,
  },
  {
    name: "vendorNotes",
    type: "string",
    description: `Vendor notes field. For in-house use.`,
  },
  {
    name: "payment",
    type: "string",
    description: `Method used to pay for these items. Must be one of "None", "Bill", "CashPurchase", "Check", "CreditCardCharge".`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "depositPercent",
    type: "decimal",
    description: `Deposit percentage field.`,
  },
  {
    name: "depositAmount",
    type: "decimal",
    description: `Deposit amount field.`,
  },
  {
    name: "subTotal",
    type: "decimal",
    description: `Subtotal for transaction.`,
    readOnly: true,
  },
  {
    name: "total",
    type: "decimal",
    description: `Transaction total.`,
    readOnly: true,
  },
  {
    name: "overhead",
    type: "decimal",
    description: `Applies a specified percentage of overhead to each item.`,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `Indicates if the summary parameter was set when retrieving back this record.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "autoSerialLots",
    type: "boolean",
    description: `Set to true to generate serial or lot numbers automatically
      when receiving such items.`,
    readOnly: true,
  },
  {
    name: "lines",
    type: "array of item receipt line items",
    description: `The lines for the item receipt.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this item receipt item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "vendorPartNumber",
    type: "string",
    description: `Self-explanatory.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line item.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "The customer this line item is targeted for, if any.",
  },
  {
    name: "tax",
    type: "tax information object",
    description: `The tax information for this line, if enabled.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity of this line item.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line item.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line item.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitprice",
    type: "decimal",
    description: `The unit price for this item.  Must be above the item’s minimum price, if set.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: `The purchase amount for this line item. The amount must equal the quantity multiplied by the unit price.`,
  },
  {
    name: "received",
    type: "decimal",
    description: `The quantity of this line item that was received.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line item.`,
  },
  {
    name: "bin",
    type: "reference",
    description: "The bin to which this line item is assigned.",
  },
  {
    name: "serials",
    type: "array of references",
    description: "The serial number(s) assigned to an item.",
  },
  {
    name: "lot",
    type: "reference",
    description: "The lot to which this line item is assigned.",
  },
  {
    name: "lotExpiration",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `The date this lot expires.`,
  },
];

export const OTHER_COST_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this other cost line item.  Must
      not be provided on create transactions.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the item receipt transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "vendor",
    type: "reference",
    description: "Vendor from which this other cost item was received.",
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line item.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: `The cost for this line.`,
  },
  {
    name: "bill",
    type: "boolean",
    description: `True if a bill should be generated to this vendor for this item. False, if not.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, id, comment, vendor name, or location name`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "location",
    type: "string",
    description: `Filters transactions according to the name of the location.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/itemreceipt?start=101&maxresults=100&location=warehouse&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
